import React, { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../supabaseClient'

const VideoThumbnail = ({ setVideos, video, showDelete }) => {
  const [hover, setHover] = useState(false)
  const videoRef = useRef(null)
  const navigate = useNavigate()

  const videoId = video?.id

  // Utility function to format view count cleanly
  const formatViews = (views) => {
    if (views === undefined || views === null) return '0 views'
    if (views >= 1000000) return `${(views / 1000000).toFixed(1)}M views`
    if (views >= 1000) return `${(views / 1000).toFixed(1)}K views`
    return `${views} views`
  }

  const mouseIn = async () => {
    setHover(true)
    if (videoRef.current) {
      try {
        await videoRef.current.play()
      } catch (err) {
        console.log("Autoplay was prevented:", err)
      }
    }
  }

  const mouseOut = () => {
    setHover(false)
    if (videoRef.current) {
      videoRef.current.pause()
      videoRef.current.currentTime = 0
    }
  }

  const handleClick = () => {
    if (!videoId) {
      console.error("Cannot navigate: video.id is undefined", video)
      return
    }
    navigate(`/${videoId}`)
  }

  const handleDelete = async (event) => {
    event.stopPropagation()
    if (!videoId) return

    try {
      const { error } = await supabase
        .from('videos')
        .delete()
        .eq('id', videoId)

      if (error) throw error

      alert('Video deleted successfully')
      setVideos((videos) => videos.filter((v) => v.id !== videoId))
    } catch (e) {
      console.error('Delete error:', e.message)
      alert('Failed to delete video.')
    }
  }

  return (
    <div
      className="relative w-full aspect-video rounded-xl overflow-hidden cursor-pointer border border-black/80 shadow-[0_4px_10px_rgba(0,0,0,0.5)] hover:shadow-[0_8px_20px_rgba(0,0,0,0.7)] hover:-translate-y-1 transition-all duration-200 bg-black group"
      onMouseEnter={mouseIn}
      onMouseLeave={mouseOut}
      onClick={handleClick}
    >
      {/* Video Element */}
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover"
        src={video?.url}
        poster={video?.thumbnail || ''}
        muted={true}
        loop={true}
        playsInline
      />

      {/* Static Image Overlay when NOT hovering */}
      {video?.thumbnail && !hover && (
        <img
          src={video.thumbnail}
          alt={video.name || 'Thumbnail'}
          className="absolute inset-0 w-full h-full object-cover z-10"
        />
      )}

      {/* Frutiger Aero Curved Top Glass Reflection */}
      <div 
        className="absolute top-[2px] left-[3%] w-[94%] h-[40%] rounded-t-[10px] rounded-b-[18px] z-15 pointer-events-none opacity-85"
        style={{
          background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0.6) 0%, rgba(255, 255, 255, 0.1) 80%, rgba(255, 255, 255, 0) 100%)'
        }}
      />

      {/* Dark Tinted Glass Box (No blur) */}
      <div className="absolute bottom-2 left-2 right-2 z-20">
        <div 
          className="relative p-2 px-3 flex items-center justify-between text-white rounded-lg border border-white/5 shadow-[0_2px_8px_rgba(0,0,0,0.5)] overflow-hidden"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.45)', // Tinted dark glass fill
            lineHeight: 1.3,
            overflowWrap: 'break-word',
            tabSize: 4
          }}
        >
          {/* Top Gloss Reflection on the glass badge itself */}
          <div 
            className="absolute top-0 left-0 right-0 h-[48%] rounded-t-lg pointer-events-none"
            style={{
              background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0.35) 0%, rgba(255, 255, 255, 0.02) 100%)'
            }}
          />

          <div className="min-w-0 flex-1 pr-2 relative z-10">
            {/* Title */}
            <div className="font-bold text-xs sm:text-sm text-white truncate drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]">
              {video?.name}
            </div>
            
            {/* Views and Date */}
            <div className="text-[11px] text-gray-200 flex items-center gap-1 mt-0.5 drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)] font-medium">
              <span>{formatViews(video?.views)}</span>
              <span>•</span>
              <span>{video?.date ? new Date(video.date).toLocaleDateString() : ''}</span>
            </div>
          </div>

          {/* Delete Button */}
          {showDelete && (
            <button 
              onClick={handleDelete} 
              className="relative z-20 p-1 text-red-400 hover:text-red-300 transition-transform hover:scale-110 drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]"
              title="Delete Video"
            >
              <i className="fa-sharp fa-solid fa-trash text-sm"></i>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default VideoThumbnail
